// QuickSearch.jsx
import { useRef, useState, useCallback } from "react";
import { Box, Button, Heading, HStack, Popover, Portal, Separator, Table, Text, Textarea } from "@chakra-ui/react";
import { LuClipboardPaste, LuCopy, LuEraser, LuFocus, LuRemoveFormatting, LuSearch } from "react-icons/lu";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";
import { createEntitySearchHighlighter } from "../../../utilies/quickSearch/Index";
import { removeEpochSuffix, removeFileExtension } from "../../../utilies/Utility";

function QuickSearch() {
  const viewerRef = useAppStore((s) => s.viewerRef);

  const [idsText, setIdsText] = useState("");
  const highlighterRef = useRef(null);
  const [resData, setResData] = useState(null);
  const [hasActiveResult, setHasActiveResult] = useState(false);

  const ensureHighlighter = useCallback(() => {
    if (!highlighterRef.current) {
      const viewer = viewerRef?.current;
      if (!viewer) return null;
      highlighterRef.current = createEntitySearchHighlighter(viewer);
    }
    return highlighterRef.current;
  }, [viewerRef]);

  const onFind = () => {
    const hl = ensureHighlighter();
    if (!hl) return;

    const res = hl.findByIdsText(idsText);
    setResData(res);

    if (res && res.matches?.length > 0) {
      setHasActiveResult(true);
    }
  };

  const onReset = () => {
    const hl = ensureHighlighter();
    if (!hl) return;

    hl.reset();
    setResData(null);
    setHasActiveResult(false);
  };

  const pasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setIdsText(text);
    } catch (err) {
      console.error("Failed to read clipboard contents: ", err);
    }
  };

  const clearTextarea = () => {
    setIdsText("");
  };

  return (
    <Popover.Root positioning={{ placement: "left-start" }}>
      <Popover.Trigger asChild>
        <Button userSelect="none" p={0} bg={hasActiveResult ? "green" : "Background"} color={hasActiveResult ? "white" : "MenuText"}>
          <Tooltip content="Quick Search">
            <LuSearch />
          </Tooltip>
        </Button>
      </Popover.Trigger>

      <Portal>
        <Popover.Positioner>
          <Popover.Content>
            <Popover.Arrow />
            <Popover.Body p={2}>
              <Heading fontSize={"lg"}>Quick Search</Heading>
              <Separator mt={1} />
              <HStack gap={0} justifyContent="space-between">
                <Text>Only searches for IDs</Text>
                <HStack gap={0}>
                  <Button size="sm" bg={"transparent"} outline={"none"} onClick={pasteFromClipboard} px={0}>
                    <LuClipboardPaste color="MenuText" />
                  </Button>
                  <Button size="sm" bg={"transparent"} outline={"none"} onClick={clearTextarea} px={0}>
                    <LuRemoveFormatting color="red" />
                  </Button>
                </HStack>
              </HStack>

              <Textarea placeholder="Enter ids ..." rows={5} value={idsText} onChange={(e) => setIdsText(e.target.value)} />

              <Box maxHeight={"350px"} overflowY={"auto"}>
                <Text fontSize="sm" mt={2}>
                  {resData
                    ? (() => {
                        const missing = Object.entries(resData.counts)
                          .filter(([, cnt]) => cnt === 0)
                          .map(([id]) => id);

                        if (missing.length === 0) return "All provided IDs matched.";

                        return (
                          <>
                            Not found:{" "}
                            {missing.map((id, i) => (
                              <span key={id}>
                                {i > 0 ? ", " : ""}
                                <code>{id}</code>
                              </span>
                            ))}
                          </>
                        );
                      })()
                    : "No results"}
                </Text>

                {resData && resData.matches?.length > 0 && (
                  <Table.Root size="sm" mt={2}>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader py={0} textAlign="start">
                          ID
                        </Table.ColumnHeader>
                        <Table.ColumnHeader py={0} textAlign="start">
                          Layer
                        </Table.ColumnHeader>
                        <Table.ColumnHeader py={0} textAlign="start">
                          Description
                        </Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {resData.matches.map((m) => (
                        <Table.Row key={`${m.searchId}__${m.entityId}`}>
                          <Table.Cell py={0} color={"MenuText"} textAlign="start" whiteSpace="nowrap">
                            <Button size="xs" variant="link" px={0} onClick={() => ensureHighlighter()?.zoomToEntityId(m.entityId)}>
                              <Tooltip content="Focus">
                                <LuFocus />
                              </Tooltip>
                            </Button>
                            <code>{m.searchId}</code>
                          </Table.Cell>

                          <Table.Cell py={0} textAlign="start" whiteSpace="nowrap">
                            <code>{removeEpochSuffix(removeFileExtension(m.layerName)) || ""}</code>
                          </Table.Cell>

                          <Table.Cell py={0} textAlign="start" whiteSpace="nowrap">
                            <Text fontSize="xs">{m.desc || ""}</Text>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                )}
              </Box>

              <HStack gap={2} mt={2}>
                <Button onClick={onFind}>
                  <LuSearch />
                  Find
                </Button>
                <Button colorPalette="red" onClick={onReset}>
                  <LuEraser />
                  Reset
                </Button>
              </HStack>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export default QuickSearch;
